/**
 * Drives the order-tracking modal rendered by `snippets/order-tracking-modal.liquid`.
 *
 * The surrounding `<modal-dialog>` (assets/global.js) already owns the overlay
 * lifecycle: moving to <body>, showing/hiding, focus trap, Escape/backdrop
 * close and scroll lock. This component only owns behaviour inside it: the
 * reCAPTCHA v2 gate, the tracking API request, and swapping between the views
 * already rendered in Liquid (`[data-view]` / `[data-status]`) while filling
 * the `[data-value]` slots with the response.
 */

const FETCH_TIMEOUT_MS = 10000;
/** The customer types only the number; the snippet supplies the prefix. */
const ORDER_PATTERN = /^\d{1,20}$/;
const RECAPTCHA_SRC = 'https://www.google.com/recaptcha/api.js?render=explicit';

/** Progress ordering of the API statuses, used to place the current step. */
const DELIVERY_ORDER = ['ready-for-handling', 'handling', 'invoiced', 'transit', 'in-distribution', 'delivered'];
const PICKUP_ORDER = ['ready-for-handling', 'handling', 'invoiced', 'pickup', 'pickup-invoiced', 'delivered'];

/** Detail fields that hold a date and need locale formatting before display. */
const DATE_FIELDS = new Set(['lastUpdate', 'expectedDate']);

/** Result-card colour per status; anything not listed reads as success. */
const CARD_TONES = { canceled: 'danger', unknown: 'neutral', split: 'neutral' };

class OrderTrackingForm extends HTMLElement {
  constructor() {
    super();

    this.content = this.closest('[data-endpoint]');
    this.endpointBase = this.content ? this.content.dataset.endpoint : '';
    this.brandCode = this.content ? this.content.dataset.brandCode : '';
    this.orderPrefix = this.content ? this.content.dataset.orderPrefix || '' : '';
    this.siteKey = this.content ? this.content.dataset.recaptchaSiteKey : '';

    this.form = this.querySelector('[data-order-form]');
    this.field = this.querySelector('[data-input-wrapper]');
    this.orderInput = this.querySelector('[data-order-input]');
    this.submitButton = this.querySelector('[data-submit]');
    this.captchaEl = this.querySelector('[data-captcha]');
    this.validationEl = this.querySelector('[data-validation]');
    this.resultsEl = this.querySelector('[data-results]');
    this.card = this.querySelector('[data-card]');
    this.resetButton = this.querySelector('[data-reset]');

    this.orderValueEl = this.card ? this.card.querySelector('[data-value="order"]') : null;
    this.detailRows = this.card ? Array.from(this.card.querySelectorAll('[data-detail-field]')) : [];
    this.shipmentsContainer = this.querySelector('[data-shipments]');
    this.shipmentTemplate = this.querySelector('[data-shipment-row-template]');
    this.timelines = Array.from(this.querySelectorAll('[data-flow]'));
    this.knownStatuses = new Set(
      Array.from(this.querySelectorAll('.order-tracking-modal__status-title [data-status]')).flatMap((node) =>
        node.dataset.status.split(' ')
      )
    );
    // Per-shipment rows need a status label to show next to each guía; reading
    // it straight from the same status-title spans keeps that copy defined
    // only once, in Liquid, instead of duplicating it here.
    this.statusLabels = new Map();
    for (const node of this.querySelectorAll('.order-tracking-modal__status-title [data-status]')) {
      const label = node.textContent.trim();
      for (const token of node.dataset.status.split(' ')) {
        this.statusLabels.set(token, label);
      }
    }

    this.widgetId = null;
    this.token = '';
    this.captchaFailed = false;
    this.abortController = null;
    this.timeoutId = null;

    if (this.form) this.form.addEventListener('submit', this.handleSubmit.bind(this));
    if (this.orderInput) this.orderInput.addEventListener('input', this.handleInput.bind(this));
    if (this.resetButton) this.resetButton.addEventListener('click', this.reset.bind(this));

    this.modal = this.closest('modal-dialog');
    // `modal-dialog` (assets/global.js) dispatches this on every close path:
    // the close button, Escape, and a backdrop click all call `hide()`.
    document.body.addEventListener('modalClosed', this.onModalClose.bind(this));

    // Load reCAPTCHA only when the modal is actually opened, not on every
    // page load: the markup is always in the DOM (just visually hidden), so
    // setting this up eagerly would make every visitor fetch Google's script
    // whether or not they ever use the tracking feature.
    if (this.modal) {
      this.openObserver = new MutationObserver(() => {
        if (this.modal.hasAttribute('open')) this.onModalOpen();
      });
      this.openObserver.observe(this.modal, { attributes: true, attributeFilter: ['open'] });
    } else {
      this.setupCaptcha();
    }

    this.updateSubmitGate();
  }

  onModalClose() {
    this.abortLookup();
    this.resetCaptcha();
    this.updateSubmitGate();
  }

  /**
   * On mobile, "RASTREA TU PEDIDO" lives inside the still-open hamburger menu
   * (`header-drawer`), so opening this modal on top of it leaves two
   * independent scroll-lock/focus-trap systems active at once. Closing that
   * menu drawer first avoids the leftover transform/lock once this modal
   * closes and the page is left looking "tilted" underneath.
   */
  onModalOpen() {
    this.setupCaptcha();

    const drawer = document.querySelector('header-drawer');
    const toggle = drawer && drawer.mainDetailsToggle;
    if (!toggle || !toggle.hasAttribute('open')) return;

    const summary = toggle.querySelector('summary');
    drawer.closeMenuDrawer(true, summary);
  }

  /* -------------------------------------------------------------- views */

  /** Shows the nodes tagged with this view and hides the rest. */
  setView(view) {
    this.dataset.view = view;
    for (const node of this.querySelectorAll('[data-view]')) {
      node.hidden = !node.dataset.view.split(' ').includes(view);
    }
  }

  /** Same idea for the status-specific copy inside the result card. */
  setStatus(status) {
    for (const node of this.querySelectorAll('[data-status]')) {
      node.hidden = !node.dataset.status.split(' ').includes(status);
    }
  }

  /* ---------------------------------------------------------- reCAPTCHA */

  async setupCaptcha() {
    if (!this.captchaEl || this.widgetId !== null || this.captchaFailed) return;
    if (!this.siteKey) {
      this.renderCaptchaError();
      return;
    }

    try {
      const grecaptcha = await loadRecaptchaScript();
      this.renderWidget(grecaptcha);
    } catch (_error) {
      this.renderCaptchaError();
    }
  }

  renderWidget(grecaptcha) {
    if (this.widgetId !== null || this.captchaFailed) return;
    try {
      this.widgetId = grecaptcha.render(this.captchaEl, {
        sitekey: this.siteKey,
        callback: () => {
          this.token = grecaptcha.getResponse(this.widgetId) || '';
          this.clearValidation();
          this.updateSubmitGate();
        },
        'expired-callback': () => {
          this.token = '';
          this.showValidation(window.orderTrackingStrings ? window.orderTrackingStrings.captchaExpired : '');
          this.updateSubmitGate();
        },
        'error-callback': () => {
          this.token = '';
          this.showValidation(window.orderTrackingStrings ? window.orderTrackingStrings.captchaError : '');
          this.updateSubmitGate();
        },
      });
      this.updateSubmitGate();
    } catch (_error) {
      this.renderCaptchaError();
    }
  }

  renderCaptchaError() {
    this.captchaFailed = true;
    if (this.captchaEl) {
      const message = document.createElement('p');
      message.className = 'order-tracking-modal__captcha-error';
      message.textContent = window.orderTrackingStrings ? window.orderTrackingStrings.captchaUnavailable : '';
      this.captchaEl.replaceChildren(message);
    }
    if (this.submitButton) this.submitButton.disabled = true;
  }

  resetCaptcha() {
    try {
      if (this.widgetId !== null && window.grecaptcha && typeof window.grecaptcha.reset === 'function') {
        window.grecaptcha.reset(this.widgetId);
      }
    } catch (_error) {
      /* no-op: widget reset is best-effort */
    }
    this.token = '';
  }

  /* -------------------------------------------------------------- gate */

  updateSubmitGate() {
    if (!this.submitButton) return;
    if (this.captchaFailed) {
      this.submitButton.disabled = true;
      return;
    }
    const hasOrder = this.orderInput ? this.orderInput.value.trim().length > 0 : false;
    this.submitButton.disabled = !(hasOrder && this.token);
  }

  handleInput() {
    this.clearValidation();
    if (this.field) this.field.classList.remove('is-invalid');
    if (this.orderInput) this.orderInput.removeAttribute('aria-invalid');
    if (this.dataset.view !== 'idle') this.setView('idle');
    this.updateSubmitGate();
  }

  /**
   * Customers are shown the prefix as a fixed affix and type only the number,
   * but plenty will paste the whole thing anyway, so a pasted prefix (and any
   * separator around it) is stripped rather than rejected.
   */
  readOrderNumber() {
    let value = this.orderInput.value.trim().replace(/\s+/g, '');
    const prefix = this.orderPrefix.replace(/[^a-z0-9]/gi, '');

    if (prefix && value.toUpperCase().startsWith(prefix.toUpperCase())) {
      value = value.slice(prefix.length);
    }
    return value.replace(/^[-\s]+/, '');
  }

  handleSubmit(event) {
    event.preventDefault();
    if (this.captchaFailed || !this.orderInput) return;

    const strings = window.orderTrackingStrings || {};
    const number = this.readOrderNumber();

    if (!ORDER_PATTERN.test(number)) {
      this.showValidation(strings.invalidOrder || '');
      this.orderInput.focus();
      return;
    }
    if (!this.token) {
      this.showValidation(strings.captchaRequired || '');
      return;
    }
    if (!this.brandCode || !this.endpointBase) {
      this.renderFailure('error');
      return;
    }

    this.orderInput.value = number;
    this.clearValidation();
    this.lookup(this.orderPrefix + number);
  }

  reset() {
    this.abortLookup();

    if (this.orderInput) this.orderInput.value = '';
    this.token = '';
    if (this.field) this.field.classList.remove('is-invalid');
    if (this.orderInput) this.orderInput.removeAttribute('aria-invalid');

    this.clearValidation();
    this.setView('idle');
    this.resetCaptcha();
    this.updateSubmitGate();

    if (this.orderInput) this.orderInput.focus();
  }

  /* -------------------------------------------------------------- fetch */

  abortLookup() {
    if (this.abortController) this.abortController.abort();
    this.abortController = null;
    clearTimeout(this.timeoutId);
    this.timeoutId = null;
  }

  async lookup(order) {
    this.abortLookup();

    this.setView('loading');
    if (this.resultsEl) this.resultsEl.setAttribute('aria-busy', 'true');
    if (this.submitButton) this.submitButton.disabled = true;

    const controller = new AbortController();
    this.abortController = controller;
    this.timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

    const url = `${this.endpointBase}${this.brandCode}/${encodeURIComponent(order)}`;

    let result = null;
    let outcome = null;

    try {
      const response = await fetch(url, {
        signal: controller.signal,
        headers: { Accept: 'application/json' },
      });

      if (response.status === 404) {
        outcome = 'not-found';
      } else if (!response.ok) {
        outcome = 'error';
      } else {
        let data;
        try {
          data = await response.json();
        } catch (_error) {
          outcome = 'error';
        }
        if (outcome === null) {
          result = this.normalize(data);
          if (!result) {
            outcome = 'error';
          } else if (result.type === 'empty') {
            outcome = 'not-found';
          } else {
            outcome = 'found';
          }
        }
      }
    } catch (_error) {
      if (this.abortController !== controller) return;
      outcome = 'error';
    }

    if (this.abortController !== controller) return;

    clearTimeout(this.timeoutId);
    this.abortController = null;
    this.resetCaptcha();
    if (this.resultsEl) this.resultsEl.setAttribute('aria-busy', 'false');

    if (outcome === 'found') {
      this.renderFound(order, result);
    } else {
      this.renderFailure(outcome);
    }

    this.updateSubmitGate();
  }

  /**
   * Normalizes the API payload. The self-service endpoint always returns an
   * array of status entries; the latest entry carries the metadata shown in
   * the detail rows. Non-array payloads violate the contract and return
   * `null`; an empty array, or one without usable statuses, maps to not-found.
   *
   * A split order comes back as one entry per package instead of one entry
   * per status change — each package carries its own `trackingNumber`. Taking
   * only the latest entry (`currentEntry`) would silently drop every guía but
   * the last, so `shipments` collects one row per distinct tracking number
   * for `renderShipments` to display them all.
   */
  normalize(data) {
    if (!Array.isArray(data)) return null;

    const entries = data.filter((item) => item && typeof item === 'object').map(toEntry);

    if (entries.length === 0) return { type: 'empty' };

    const tracked = entries.filter((entry) => entry.status);

    if (tracked.length === 0) {
      return {
        type: 'status',
        status: 'unknown',
        entry: entries[entries.length - 1],
        shipments: collectShipments(entries),
      };
    }

    const statuses = tracked.map((entry) => entry.status);
    const current = statuses[statuses.length - 1];
    const currentEntry = tracked[tracked.length - 1];
    const shipments = collectShipments(tracked);

    if (statuses.includes('canceled')) {
      const canceled = tracked.find((entry) => entry.status === 'canceled') || currentEntry;
      return { type: 'status', status: 'canceled', entry: canceled, shipments };
    }

    if (!this.knownStatuses.has(current)) return null;

    const isPickup = current === 'pickup' || current === 'pickup-invoiced';
    const orderList = isPickup ? PICKUP_ORDER : DELIVERY_ORDER;

    if (!orderList.includes(current)) return { type: 'status', status: current, entry: currentEntry, shipments };

    return { type: 'timeline', status: current, isPickup, entry: currentEntry, shipments };
  }

  /* ---------------------------------------------------------- rendering */

  renderFound(order, result) {
    const shipments = result.shipments || [];
    // Each package in a split order can be at a different stage, so a single
    // headline status would misrepresent whichever one isn't "current". The
    // synthetic "split" status swaps the headline for a neutral one and lets
    // renderShipments/markTimeline show each package's real status instead.
    const isSplit = shipments.length > 1;
    const displayStatus = isSplit ? 'split' : result.status;

    if (this.card) this.card.dataset.tone = CARD_TONES[displayStatus] || 'success';
    if (this.orderValueEl) this.orderValueEl.textContent = order;

    this.setStatus(displayStatus);
    this.fillDetails(result.entry);
    this.renderShipments(shipments);
    this.markTimeline(result, isSplit);
    this.setView('found');
  }

  fillDetails(entry) {
    for (const row of this.detailRows) {
      const field = row.dataset.detailField;
      const raw = entry[field] || '';
      const value = DATE_FIELDS.has(field) ? formatDate(raw) : raw;

      const slot = row.querySelector('[data-value]');
      if (slot) slot.textContent = value;
      row.hidden = !value;
    }
  }

  /**
   * Renders one row per shipment so a split order shows every guía instead of
   * only the last package's. A single shipment keeps the original two-row
   * layout (courier, then tracking number); more than one gets a numbered
   * "Envío N" row per package, each with its own status — the packages can
   * be at different stages, so this is the only place that status is visible.
   */
  renderShipments(shipments) {
    if (!this.shipmentsContainer) return;
    this.shipmentsContainer.replaceChildren();
    if (!this.shipmentTemplate || shipments.length === 0) return;

    const strings = window.orderTrackingStrings || {};

    if (shipments.length === 1) {
      const [shipment] = shipments;
      this.appendShipmentRow(strings.shipmentCourierLabel || '', shipment.courierName);
      this.appendShipmentRow(strings.shipmentTrackingLabel || '', shipment.trackingNumber);
      return;
    }

    shipments.forEach((shipment, index) => {
      const label = `${strings.shipmentLabel || ''} ${index + 1}`.trim();
      const statusLabel = this.statusLabels.get(shipment.status) || shipment.status;
      const trackingPart = `${strings.shipmentTrackingWord || ''} ${shipment.trackingNumber}`.trim();
      const courierPart = shipment.courierName ? `${shipment.courierName} — ${trackingPart}` : trackingPart;
      const summary = statusLabel ? `${statusLabel} · ${courierPart}` : courierPart;
      this.appendShipmentRow(label, summary);
    });
  }

  appendShipmentRow(label, value) {
    if (!value || !this.shipmentTemplate || !this.shipmentsContainer) return;

    const fragment = this.shipmentTemplate.content.cloneNode(true);
    const labelSlot = fragment.querySelector('[data-value="label"]');
    const valueSlot = fragment.querySelector('[data-value="value"]');
    if (labelSlot) labelSlot.textContent = label;
    if (valueSlot) valueSlot.textContent = value;
    this.shipmentsContainer.appendChild(fragment);
  }

  /** `isSplit` hides the single timeline: with multiple packages there is no one "current step" to show. */
  markTimeline(result, isSplit) {
    const flow = result.isPickup ? 'pickup' : 'delivery';

    for (const list of this.timelines) {
      const active = !isSplit && result.type === 'timeline' && list.dataset.flow === flow;
      list.hidden = !active;
      if (!active) continue;

      const steps = Array.from(list.children);
      const currentIndex = steps.findIndex((step) => step.dataset.match.split(' ').includes(result.status));

      steps.forEach((step, index) => {
        step.classList.toggle('is-done', index < currentIndex);
        step.classList.toggle('is-current', index === currentIndex);
        if (index === currentIndex) step.setAttribute('aria-current', 'step');
        else step.removeAttribute('aria-current');
      });
    }
  }

  /** Keeps the form on screen with the typed number so it can be fixed in place. */
  renderFailure(view) {
    this.setView(view);
    if (this.field) this.field.classList.add('is-invalid');
    if (this.orderInput) {
      this.orderInput.setAttribute('aria-invalid', 'true');
      this.orderInput.focus();
      this.orderInput.select();
    }
  }

  showValidation(message) {
    if (this.validationEl) this.validationEl.textContent = message;
  }

  clearValidation() {
    if (this.validationEl) this.validationEl.textContent = '';
  }
}

if (!customElements.get('order-tracking-form')) {
  customElements.define('order-tracking-form', OrderTrackingForm);
}

/** Reads one API item, defaulting every field to a string. */
function toEntry(item) {
  const text = (value) => (typeof value === 'string' ? value : '');

  return {
    orderId: text(item.orderId),
    status: text(item.status),
    lastUpdate: text(item.lastUpdate),
    expectedDate: text(item.expectedDate),
    trackingNumber: text(item.trackingNumber),
    courierName: text(item.courierName),
  };
}

/**
 * Collects one row per distinct tracking number found in the API entries. A
 * split order reports one entry per package instead of one per status
 * change, so a later entry sharing an earlier package's tracking number
 * overwrites it here to keep that package's most current data.
 */
function collectShipments(entries) {
  const byTrackingNumber = new Map();
  for (const entry of entries) {
    if (!entry.trackingNumber) continue;
    byTrackingNumber.set(entry.trackingNumber, entry);
  }
  return Array.from(byTrackingNumber.values());
}

/** Formats `YYYY-MM-DD`, `YYYY-MM-DD HH:mm:ss` or ISO as a Colombian long date. */
function formatDate(value) {
  if (!value) return '';

  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value.replace(' ', 'T');
  const date = new Date(normalized);

  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('es-CO', { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
}

/**
 * Loads the reCAPTCHA explicit script exactly once across all component
 * instances and resolves with the global `grecaptcha` object.
 *
 * @returns {Promise<typeof window.grecaptcha>}
 */
function loadRecaptchaScript() {
  if (window.grecaptcha && typeof window.grecaptcha.render === 'function') {
    return Promise.resolve(window.grecaptcha);
  }

  if (loadRecaptchaScript.promise) return loadRecaptchaScript.promise;

  loadRecaptchaScript.promise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${RECAPTCHA_SRC}"]`);
    const script = existing || document.createElement('script');

    const cleanup = () => {
      script.removeEventListener('load', onLoad);
      script.removeEventListener('error', onError);
    };

    const onLoad = () => {
      const grecaptcha = window.grecaptcha;
      if (grecaptcha && typeof grecaptcha.render === 'function') {
        cleanup();
        resolve(grecaptcha);
      } else if (grecaptcha && typeof grecaptcha.ready === 'function') {
        grecaptcha.ready(() => {
          cleanup();
          resolve(grecaptcha);
        });
      } else {
        cleanup();
        reject(new Error('reCAPTCHA unavailable'));
      }
    };

    const onError = () => {
      cleanup();
      reject(new Error('reCAPTCHA script failed to load'));
    };

    script.addEventListener('load', onLoad);
    script.addEventListener('error', onError);

    if (!existing) {
      script.src = RECAPTCHA_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });

  return loadRecaptchaScript.promise;
}
